import { apiClient } from '@/modules/shared/services/apiClient';
import type { ApiResponse } from '@/modules/shared/types';
import type {
  LegalPageListItem,
  LegalPageDetail,
  CreateLegalPageRequest,
  UpdateLegalPageRequest,
  PublicLegalPage,
  LegalPageLink,
} from '@/modules/shared/types/legalPage';

export const legalPageService = {
  // ─── Admin endpoints ───────────────────────────────────────

  async getAll(programId?: string | null): Promise<ApiResponse<LegalPageListItem[]>> {
    const params = programId ? { programId } : {};
    const response = await apiClient.get<ApiResponse<LegalPageListItem[]>>('/legal-pages', { params });
    return response.data;
  },

  async getById(id: string): Promise<ApiResponse<LegalPageDetail>> {
    const response = await apiClient.get<ApiResponse<LegalPageDetail>>(`/legal-pages/${id}`);
    return response.data;
  },

  async create(data: CreateLegalPageRequest): Promise<ApiResponse<LegalPageDetail>> {
    const response = await apiClient.post<ApiResponse<LegalPageDetail>>('/legal-pages', data);
    return response.data;
  },

  async update(id: string, data: UpdateLegalPageRequest): Promise<ApiResponse<LegalPageDetail>> {
    const response = await apiClient.put<ApiResponse<LegalPageDetail>>(`/legal-pages/${id}`, data);
    return response.data;
  },

  async publish(id: string): Promise<ApiResponse<LegalPageDetail>> {
    const response = await apiClient.put<ApiResponse<LegalPageDetail>>(`/legal-pages/${id}/publish`);
    return response.data;
  },

  async delete(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    const response = await apiClient.delete<ApiResponse<{ deleted: boolean }>>(`/legal-pages/${id}`);
    return response.data;
  },

  // ─── Public endpoints ──────────────────────────────────────

  async getPublicPage(pageKey: string, programId?: string | null): Promise<ApiResponse<PublicLegalPage>> {
    const params = programId ? { programId } : {};
    const response = await apiClient.get<ApiResponse<PublicLegalPage>>(`/legal-pages/public/${encodeURIComponent(pageKey)}`, { params });
    return response.data;
  },

  async getLoginLinks(programCode?: string | null): Promise<ApiResponse<LegalPageLink[]>> {
    const params = programCode ? { programCode } : {};
    const response = await apiClient.get<ApiResponse<LegalPageLink[]>>('/legal-pages/public/login', { params });
    return response.data;
  },

  async getFooterLinks(programId?: string | null): Promise<ApiResponse<LegalPageLink[]>> {
    const params = programId ? { programId } : {};
    const response = await apiClient.get<ApiResponse<LegalPageLink[]>>('/legal-pages/public/footer', { params });
    return response.data;
  },

  async getUserMenuLinks(programId?: string | null): Promise<ApiResponse<LegalPageLink[]>> {
    const params = programId ? { programId } : {};
    const response = await apiClient.get<ApiResponse<LegalPageLink[]>>('/legal-pages/public/user-menu', { params });
    return response.data;
  },
};
